import React, { useState, useEffect } from 'react'
import CommentTile from '../CommentTile/CommentTile'
import { supabase } from '../../supabaseClient'
import { useDispatch, useSelector } from 'react-redux'

const CommentSection = ({ video }) => {
  const [comments, setComments] = useState([])
  const dispatch = useDispatch()
  const isLogged = useSelector((state) => state.user.isLoggedIn)
  const activeUsername = localStorage.getItem('currentUser')

  const targetVideoId = video?.key ?? video?.id ?? video?._id

  // Format date cleanly to YYYY-MM-DD
  const formatDate = (rawDate) => {
    if (!rawDate) return ""
    try {
      const d = new Date(rawDate)
      return isNaN(d.getTime()) ? String(rawDate).split('T')[0] : d.toISOString().split('T')[0]
    } catch (e) {
      return String(rawDate).split('T')[0]
    }
  }

  const getCommentsArray = async () => {
    if (!targetVideoId) return

    const { data: commentsData } = await supabase
      .from('comments')
      .select('*')
      .eq('video_id', Number(targetVideoId))
      .order('id', { ascending: false })

    if (!commentsData) {
      setComments([])
      return
    }

    setComments(
      commentsData.map((c) => ({
        key: c.id,
        id: c.id,
        username: c.username || c.user_id || 'User',
        date: formatDate(c.date || c.created_at || Date.now()),
        text: c.text,
        comment: c.text,
        reply_count: c.reply_count || 0
      }))
    )
  }

  const handleAddComment = async () => {
    if (!isLogged || !activeUsername) {
      dispatch({ type: "SHOW_AUTH" })
      return
    }

    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id')
      .ilike('user_id', activeUsername)

    if (!profiles || profiles.length === 0) {
      alert("You must create a profile before commenting.")
      dispatch({ type: "SHOW_AUTH" })
      return
    }

    const ele = document.getElementById('comment-input')
    const commentText = ele ? ele.value.trim() : ''

    if (commentText.length > 0) {
      const { error } = await supabase
        .from('comments')
        .insert([
          {
            video_id: Number(targetVideoId),
            user_id: activeUsername,
            username: activeUsername,
            text: commentText
          }
        ])

      if (error) {
        alert("Failed to add comment: " + error.message)
      } else {
        ele.value = ''
        getCommentsArray()
      }
    } else {
      alert('Write a comment')
    }
  }

  useEffect(() => {
    if (video) {
      getCommentsArray()
    }
  }, [video])

  // Custom Dark Teal Metallic Gradient
  const darkTealButtonStyle = {
    background: 'linear-gradient(to bottom, #1d4ed8 0%, #0f766e 0%, #115e59 50%, #042f2e 100%)',
    boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.25), 0px 2px 5px rgba(0,0,0,0.4)',
    border: '1px solid #042f2e'
  }

  return (
    <div className="flex flex-col mt-8 max-w-4xl mx-auto w-full">
      {/* Comments Heading - Aligned with mx-4 matching the video title */}
      <div className="mx-4 font-bold text-lg text-gray-900 pb-2">
        Comments
      </div>

      {/* Input Row - Aligned precisely with the left edge of title */}
      <div className="mx-4 flex flex-col sm:flex-row items-center gap-5 mb-6">
        <input 
          className="w-full sm:w-1/2 max-w-96 rounded-md h-9 p-2.5 text-sm border border-gray-400 bg-white shadow-inner focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 transition-all text-gray-900 placeholder-gray-400" 
          required 
          type="text" 
          id="comment-input" 
          placeholder="Add a comment..."
        />
        <button 
          style={darkTealButtonStyle}
          className="text-teal-50 w-fit h-9 px-5 rounded-md text-sm font-semibold transition-all hover:brightness-110 active:scale-95" 
          onClick={handleAddComment}
        >
          Add Comment
        </button>
      </div>

      {/* Comments List */}
      <div className="mx-4">
        {comments.map((comment) => (
          <CommentTile 
            key={comment.key} 
            comment={comment} 
            setComments={setComments} 
          />
        ))}
      </div>
    </div>
  )
}

export default CommentSection
